import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Plus, Trash2 } from 'lucide-react'
import { useState } from 'react'
import { cn } from '../lib/cn'
import { poolsQuery, workspacesQuery } from '../lib/queries'
import { aktiverWorkspaceIst, trpc } from '../lib/trpc'

/**
 * **Pools anlegen und bearbeiten** – Z-07.16
 * ([Z-07](../../../../docs/phasen/zusatz-07-workspaces.md)).
 *
 * Nils, 25.08.2026: *„Pools soll man in seinem privat selber anlegen dürfen. Der Admin auch für
 * geteilte Workspaces."* Und: *„Sortierung ist wichtig, dass man die «Sofort» Pools auch
 * imitieren kann."*
 *
 * ## Warum hier keine Sonderrolle für „Sofort" steht
 *
 * Weil es keine gibt. Die Spielregeln sind seit Phase 4 **Spalten**, und der Code liest
 * durchgehend sie statt der Kennung: `darfStarten` prüft `poolDurchbrichtWip`,
 * `sofortSichtbarFuer` prüft `durchbrichtWip`, der Trichter prüft `ziehbar`. Ein Pool mit
 * **„geht immer vor"** und Reihenfolge 1 *ist* ein Sofort-Vorrat
 * ([E-236](../../../../docs/ENTSCHEIDUNGEN.md#e-236)) – diese Maske schaltet nur die Spalten
 * frei, die es ohnehin gab.
 *
 * ## Was die Zahl in „Reihenfolge" bedeutet
 *
 * Sie ist die **Wichtigkeit**, nicht die Anzeigeposition: „Was als Nächstes" arbeitet die Pools
 * in dieser Reihenfolge ab, und der Trichter liest sie von unten nach oben. Kleiner heißt
 * weiter vorn. Deshalb steht sie als Zahl da und nicht als Ziehgriff – eine Liste, die man
 * sortiert, sagt nicht, *wie weit* etwas vorn ist.
 */
export function PoolVerwaltung() {
  const queryClient = useQueryClient()
  const { data: pools } = useQuery(poolsQuery)
  const { data: workspaces } = useQuery(workspacesQuery)
  const [neu, setNeu] = useState(false)
  const [fehler, setFehler] = useState<string | null>(null)

  const aktiv = aktiverWorkspaceIst() ?? workspaces?.[0]?.id ?? null
  const welt = workspaces?.find((w) => w.id === aktiv)

  const frisch = () => void queryClient.invalidateQueries()
  const melde = (e: unknown) => setFehler(e instanceof Error ? e.message : 'Das ging nicht.')

  const anlegen = useMutation({
    mutationFn: (v: { label: string; sortOrder: number; wipDurchbrechen: boolean }) =>
      trpc.pools.anlegen.mutate({ workspaceId: aktiv ?? '', ...v }),
    onSuccess: () => {
      setNeu(false)
      setFehler(null)
      frisch()
    },
    onError: melde,
  })
  const aendern = useMutation({
    mutationFn: (v: Parameters<typeof trpc.pools.aendern.mutate>[0]) =>
      trpc.pools.aendern.mutate(v),
    onSuccess: frisch,
    onError: melde,
  })
  const loeschen = useMutation({
    mutationFn: (id: string) => trpc.pools.loeschen.mutate({ id }),
    onSuccess: frisch,
    onError: melde,
  })

  return (
    <div className="rounded-xl border border-line bg-white p-4">
      <p className="mb-3 text-[12.5px] text-muted">
        Die Pools von <strong>{welt?.label ?? 'dieser Arbeitswelt'}</strong>. Wer eine andere
        bearbeiten will, schaltet links um.
      </p>

      {fehler && (
        <div className="mb-3 rounded-lg bg-[#fdf1f1] px-3 py-2 text-[12.5px] text-pool-sofort">
          {fehler}
        </div>
      )}

      <div className="overflow-x-auto">
        <table className="min-w-[640px] w-full text-[13px]">
          <thead>
            <tr className="border-b border-line text-left text-[11px] text-muted uppercase">
              <th className="pb-2 font-semibold">Pool</th>
              <th
                className="tabular pb-2 text-right font-semibold"
                title="Kleiner heißt weiter vorn"
              >
                Reihenfolge
              </th>
              <th
                className="pb-2 text-center font-semibold"
                title="Darf das persönliche WIP-Limit durchbrechen – so wirkt „Sofort“"
              >
                Geht vor
              </th>
              <th className="pb-2 text-center font-semibold" title="Wird hier überhaupt gezogen?">
                Vorrat
              </th>
              {/**
               * **„Vorlauf nur mit Termin"** (16.09.2026, E-353). Nils: *„Ideen den Pool will
               * ich da nicht anzeigen … wenn die Aufgaben kein Zeitfenster, keine Fälligkeit
               * haben."* Der Pool bleibt Vorrat; „Mein Tag" schlägt daraus nur vor, was einen
               * Termin trägt. Eine dritte Spielregel-Spalte, keine Sonderrolle für „Ideen".
               */}
              <th
                className="pb-2 text-center font-semibold"
                title="„Mein Tag“ schlägt aus diesem Pool nur vor, was eine Fälligkeit oder ein Zeitfenster hat"
              >
                Vorlauf nur mit Termin
              </th>
              <th className="pb-2 text-right font-semibold" />
            </tr>
          </thead>
          <tbody>
            {pools?.map((p) => (
              <tr key={p.id} className="border-b border-line/60">
                <td className="py-2">
                  <span className="mr-1.5">{p.icon}</span>
                  <span className="font-semibold">{p.label}</span>
                  <code className="ml-2 text-[11px] text-muted">{p.id}</code>
                </td>
                <td className="py-2 text-right">
                  <input
                    type="number"
                    min={0}
                    max={999}
                    defaultValue={p.sortOrder}
                    onBlur={(e) => {
                      const wert = Number(e.target.value)
                      if (wert !== p.sortOrder) aendern.mutate({ id: p.id, sortOrder: wert })
                    }}
                    className="tabular w-16 rounded border border-line px-1.5 py-0.5 text-right"
                  />
                </td>
                <td className="py-2 text-center">
                  <input
                    type="checkbox"
                    checked={p.wipDurchbrechen}
                    onChange={(e) =>
                      aendern.mutate({ id: p.id, wipDurchbrechen: e.target.checked })
                    }
                  />
                </td>
                <td className="py-2 text-center">
                  <input
                    type="checkbox"
                    checked={p.ziehbar}
                    onChange={(e) => aendern.mutate({ id: p.id, ziehbar: e.target.checked })}
                  />
                </td>
                <td className="py-2 text-center">
                  <input
                    type="checkbox"
                    aria-label={`Vorlauf nur mit Termin: ${p.label}`}
                    checked={p.vorlaufNurMitTermin}
                    onChange={(e) =>
                      aendern.mutate({ id: p.id, vorlaufNurMitTermin: e.target.checked })
                    }
                  />
                </td>
                <td className="py-2 text-right">
                  {/**
                   * **Der Parkplatz hat keinen Löschknopf.** „Parken" braucht ihn und bräche ohne
                   * ihn ab – mitten in der Arbeit, beim Klick. Der Server lehnt es ohnehin ab;
                   * hier steht der Knopf gar nicht erst, damit niemand eine Absage sucht.
                   */}
                  {p.schluessel !== 'parkplatz' && (
                    <button
                      type="button"
                      onClick={() => loeschen.mutate(p.id)}
                      className="rounded-lg p-1.5 text-muted hover:bg-[#fdf1f1] hover:text-pool-sofort"
                      title="Löschen – geht nur, solange nichts drin liegt"
                    >
                      <Trash2 className="size-[15px]" />
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {neu ? (
        <NeuerPool
          anlegen={(v) => anlegen.mutate(v)}
          abbrechen={() => setNeu(false)}
          laeuft={anlegen.isPending}
        />
      ) : (
        <button
          type="button"
          onClick={() => setNeu(true)}
          className="mt-3 flex items-center gap-1.5 rounded-lg px-2 py-1.5 text-[12.5px] font-semibold text-accent-deep hover:bg-accent-soft"
        >
          <Plus className="size-[15px]" /> Pool anlegen
        </button>
      )}
    </div>
  )
}

function NeuerPool({
  anlegen,
  abbrechen,
  laeuft,
}: {
  anlegen: (v: { label: string; sortOrder: number; wipDurchbrechen: boolean }) => void
  abbrechen: () => void
  laeuft: boolean
}) {
  const [label, setLabel] = useState('')
  const [sortOrder, setSortOrder] = useState(50)
  const [wipDurchbrechen, setWip] = useState(false)

  return (
    <div className="mt-3 grid gap-2 border-t border-line pt-3">
      <div className="flex items-center gap-2">
        <input
          autoFocus
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          placeholder="Name des Pools"
          aria-label="Name des Pools"
          className="flex-1 rounded-lg border border-line px-3 py-1.5 text-[13px]"
        />
        <label className="flex items-center gap-1.5 text-[12px] text-muted">
          Reihenfolge
          <input
            type="number"
            min={0}
            max={999}
            value={sortOrder}
            onChange={(e) => setSortOrder(Number(e.target.value))}
            className="tabular w-16 rounded border border-line px-1.5 py-1 text-right"
          />
        </label>
      </div>
      <label className="flex items-start gap-2 text-[12px] text-ink2">
        <input
          type="checkbox"
          checked={wipDurchbrechen}
          onChange={(e) => setWip(e.target.checked)}
          className="mt-0.5"
        />
        <span>
          <strong>Geht immer vor</strong> – darf das persönliche WIP-Limit durchbrechen.
          <span className="block text-muted">
            Zusammen mit Reihenfolge 1 ist das ein eigener „Sofort“-Vorrat.
          </span>
        </span>
      </label>
      <div className="flex gap-2">
        <button
          type="button"
          disabled={label.trim().length < 2 || laeuft}
          onClick={() => anlegen({ label: label.trim(), sortOrder, wipDurchbrechen })}
          className={cn(
            'rounded-lg bg-accent px-3 py-1.5 text-[12.5px] font-semibold text-white',
            'disabled:opacity-40',
          )}
        >
          Anlegen
        </button>
        <button
          type="button"
          onClick={abbrechen}
          className="rounded-lg px-3 py-1.5 text-[12.5px] text-muted hover:bg-[#f3f2ef]"
        >
          Abbrechen
        </button>
      </div>
    </div>
  )
}
